import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import createAxiosInstance, {toQueryParams} from '../support/AxiosSupport';
import {fetchRef} from '../service/refsCache';
import {useWardrobe} from '../context/WardrobeContext';
import {useToast} from '../context/ToastContext';
import AuthImage from '../components/AuthImage';
import Spinner from '../components/Spinner';
import OutfitScoreBadge, {OutfitScoreDiagnostic} from '../components/OutfitScoreBadge';
import OutfitScoreDetails from '../components/OutfitScoreDetails';
import WearStateControls, {CategoryCapability, WearState, toWearStatePayload} from '../components/WearStateControls';
import {CheckCheckIcon, RefreshIcon, SparklesIcon, XIcon} from '../components/icons';
import BuilderCompatPanel, {CompatCandidate, CompatWarning} from '../components/outfit-builder/BuilderCompatPanel';
import OutfitReadinessSignals from '../components/outfit-builder/OutfitReadinessSignals';
import {
    DONE_STEP,
    SLOT_STEP,
    STEP_SLOT,
    STEPS,
    StepDef,
    StepKey,
    emptySelections,
} from '../components/outfit-builder/steps';
import {clearDraft, loadDraft, saveDraft} from '../components/outfit-builder/builderDraft';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {pluralItems} from '../utils/plural';

// ── types ──────────────────────────────────────────────────────────────────────

interface RefOption {
    id: string;
    code: string;
    nameRu: string;
}

interface WardrobeItem {
    id: string;
    name: string;
    brandName: string | null;
    categoryId?: string | null;
    categoryName: string | null;
    firstPhotoUrl: string | null;
    rating: number | null;
}

interface PageResponse {
    content: WardrobeItem[];
    totalElements: number;
}

/** Ответ `POST /api/compatibility/for-set`: кандидаты под слот и характеристики комплекта. */
interface CompatibilitySetResult {
    setItemIds: string[];
    slot: string | null;
    setWarnings: CompatWarning[];
    sharedOccasions: string[];
    sharedSeasons: string[];
    occasionDataComplete: boolean;
    seasonDataComplete: boolean;
    items: CompatCandidate[];
}

interface OutfitItemResp {
    id: string;
    name: string;
    brandName: string | null;
    rating: number | null;
    primaryPhotoUrl: string | null;
    slot: string | null;
    categoryId?: string | null;
    wearState?: WearState | null;
}

interface SavedOutfitResp {
    id: string;
    name: string;
    items: OutfitItemResp[];
}

/**
 * Предзаполнение из подбора образа дня («Дополнить» на неполном образе, QA-1) и с карточки
 * вещи («Собрать образ», CTHS-315): вещи со слотами передаются через
 * `location.state.prefillItems`, конструктор открывается на первом незакрытом обязательном шаге.
 */
export interface BuilderPrefillItem {
    id: string;
    name: string;
    brandName: string | null;
    primaryPhotoUrl: string | null;
    slot: string | null;
    categoryId?: string | null;
}

// ── helpers ────────────────────────────────────────────────────────────────────

function codesToIds(refs: RefOption[], codes: string[]): string[] {
    const set = new Set(codes);
    return refs.filter(r => set.has(r.code)).map(r => r.id);
}

/** Первый незаполненный обязательный шаг до `index`; `null` — путь до шага открыт (F12). */
function blockingStep(selections: Record<StepKey, WardrobeItem[]>, index: number): StepDef | null {
    const limit = Math.min(index, STEPS.length);
    for (let i = 0; i < limit; i++) {
        if (STEPS[i].required && selections[STEPS[i].key].length === 0) {
            return STEPS[i];
        }
    }
    return null;
}

// ── component ──────────────────────────────────────────────────────────────────

const OutfitBuilderPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {showToast} = useToast();
    const [searchParams] = useSearchParams();
    const editOutfitId = searchParams.get('outfitId');
    const replaceSlot = searchParams.get('slot');
    const replaceItemId = searchParams.get('replaceItemId');
    const {selectedOwnerId, isOwnWardrobe, permissions} = useWardrobe();
    const canManage = isOwnWardrobe || (permissions?.includes('MANAGE_LISTS') ?? false);

    const [currentStep, setCurrentStep] = useState(0);
    const [selections, setSelections] = useState<Record<StepKey, WardrobeItem[]>>(emptySelections<WardrobeItem>());

    const [stepItems, setStepItems] = useState<WardrobeItem[]>([]);
    const [stepLoading, setStepLoading] = useState(false);
    const [stepSearch, setStepSearch] = useState('');
    /** Подсказка степпера при попытке уйти на недоступный шаг (F12). */
    const [stepHint, setStepHint] = useState<string | null>(null);

    const [setInfo, setSetInfo] = useState<CompatibilitySetResult | null>(null);
    const [compatLoading, setCompatLoading] = useState(false);
    const [compatError, setCompatError] = useState<string | null>(null);
    const [compatReload, setCompatReload] = useState(0);

    const [categoryRefs, setCategoryRefs] = useState<RefOption[]>([]);
    const [refsLoaded, setRefsLoaded] = useState(false);

    // wear-state: capabilities по категориям и состояние ношения выбранных вещей.
    const [capabilities, setCapabilities] = useState<Record<string, CategoryCapability>>({});
    const [wearStates, setWearStates] = useState<Record<string, WearState | null>>({});

    const [outfitName, setOutfitName] = useState('');
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

    const cancelRef = useRef(false);
    const prefilledRef = useRef(false);
    const replaceAppliedRef = useRef(false);
    /** Черновик читается ровно один раз, до сетевых запросов (F01). */
    const draftLoadedRef = useRef(false);

    useDocumentTitle(editOutfitId ? 'Редактирование образа' : undefined);

    // ── черновик (F01) ──────────────────────────────────────────────────────────
    //
    // Аудит: клик по совместимой вещи уводил на карточку, возврат браузерной «Назад»
    // возвращал пустой конструктор на первом шаге. Теперь состав, шаг и имя живут в
    // sessionStorage вкладки и восстанавливаются при возврате и при перезагрузке.

    useEffect(() => {
        if (draftLoadedRef.current) {
            return;
        }
        draftLoadedRef.current = true;
        const draft = loadDraft<WearState>(selectedOwnerId, editOutfitId);
        if (!draft) {
            return;
        }
        // Черновик выигрывает у режима редактирования и у prefill: это более свежая работа.
        prefilledRef.current = true;
        setSelections({...emptySelections<WardrobeItem>(), ...draft.selections});
        setWearStates(draft.wearStates ?? {});
        setOutfitName(draft.outfitName ?? '');
        setCurrentStep(Math.min(Math.max(draft.step, 0), DONE_STEP));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const allSelectedItems: WardrobeItem[] = [
        ...selections.base,
        ...selections.mid,
        ...selections.outer,
        ...selections.bottom,
        ...selections.footwear,
        ...selections.accessories,
    ];
    const totalSelected = allSelectedItems.length;
    const selectedIdsKey = allSelectedItems.map(i => i.id).join(',');

    useEffect(() => {
        if (!draftLoadedRef.current || saveStatus === 'saved') {
            return;
        }
        if (totalSelected === 0 && outfitName.trim() === '' && currentStep === 0) {
            clearDraft();
            return;
        }
        saveDraft<WearState>({
            ownerId: selectedOwnerId,
            outfitId: editOutfitId,
            step: currentStep,
            selections,
            outfitName,
            wearStates,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep, selections, outfitName, wearStates, selectedOwnerId, editOutfitId, saveStatus]);

    // Режим редактирования/замены: подгружаем существующий образ и предзаполняем выбор.
    useEffect(() => {
        if (!editOutfitId || prefilledRef.current) return;
        prefilledRef.current = true;
        (async () => {
            try {
                const api = await createAxiosInstance();
                const params = selectedOwnerId ? {ownerId: selectedOwnerId} : {};
                const res = await api.get<SavedOutfitResp>(`/outfits/saved/${editOutfitId}`, {params});
                const sel = emptySelections<WardrobeItem>();
                const loadedStates: Record<string, WearState | null> = {};
                for (const it of res.data.items) {
                    const stepKey = it.slot ? SLOT_STEP[it.slot] : undefined;
                    if (!stepKey) continue;
                    sel[stepKey].push({
                        id: it.id, name: it.name, brandName: it.brandName, categoryId: it.categoryId ?? null,
                        categoryName: null, firstPhotoUrl: it.primaryPhotoUrl, rating: it.rating,
                    });
                    if (it.wearState) loadedStates[it.id] = it.wearState;
                }
                setSelections(sel);
                setWearStates(loadedStates);
                setOutfitName(res.data.name);
                if (replaceSlot && SLOT_STEP[replaceSlot]) {
                    const idx = STEPS.findIndex(s => s.key === SLOT_STEP[replaceSlot]);
                    if (idx >= 0) setCurrentStep(idx);
                }
            } catch {
                /* образ не загрузился — начинаем с пустого конструктора */
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editOutfitId, selectedOwnerId]);

    // Предзаполнение из подбора образа дня и с карточки вещи: неполный образ приходит со
    // слотами, конструктор стартует с первого незакрытого обязательного шага.
    useEffect(() => {
        if (editOutfitId || prefilledRef.current) return;
        const prefill = (location.state as {prefillItems?: BuilderPrefillItem[]} | null)?.prefillItems;
        if (!prefill || prefill.length === 0) return;
        prefilledRef.current = true;
        const sel = emptySelections<WardrobeItem>();
        for (const it of prefill) {
            const stepKey = it.slot ? SLOT_STEP[it.slot] : undefined;
            if (!stepKey) continue;
            const def = STEPS.find(st => st.key === stepKey);
            if (def && !def.multiple && sel[stepKey].length > 0) continue;
            sel[stepKey].push({
                id: it.id, name: it.name, brandName: it.brandName, categoryId: it.categoryId ?? null,
                categoryName: null, firstPhotoUrl: it.primaryPhotoUrl, rating: null,
            });
        }
        setSelections(sel);
        const firstMissing = STEPS.findIndex(st => st.required && sel[st.key].length === 0);
        setCurrentStep(firstMissing >= 0 ? firstMissing : DONE_STEP);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state, editOutfitId]);

    // Загружаем ВСЕ возможные категории (не только те, что есть в гардеробе).
    // Это критично: filter-options возвращает только категории из гардероба пользователя,
    // и если в нём нет ни одного аксессуара, accessory-коды не найдутся → фильтр пустой
    // → API возвращает все вещи. Глобальный /refs/categories всегда содержит все коды.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const cats = await fetchRef<RefOption[]>('categories');
                if (!cancelled) { setCategoryRefs(cats); setRefsLoaded(true); }
            } catch {
                if (!cancelled) setRefsLoaded(true);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // wear-state: справочник capabilities по категориям (какие переключатели доступны).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const caps = await fetchRef<CategoryCapability[]>('category-capabilities');
                if (!cancelled) {
                    const byCategory: Record<string, CategoryCapability> = {};
                    for (const cap of caps) byCategory[cap.categoryId] = cap;
                    setCapabilities(byCategory);
                }
            } catch {
                /* без справочника переключатели состояний просто скрыты */
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Загрузка вещей для текущего шага
    const loadStepItems = useCallback(async (step: number, search: string) => {
        if (step >= STEPS.length || !refsLoaded) return;
        const def = STEPS[step];

        const categoryIds = codesToIds(categoryRefs, def.categoryCodes);

        // Если для шага определены коды, но ни один не найден в рефах — показываем пустоту,
        // а не все вещи (пустой массив categoryIds = отсутствие фильтра = все вещи).
        if (def.categoryCodes.length > 0 && categoryIds.length === 0) {
            setStepItems([]);
            setStepLoading(false);
            return;
        }

        setStepLoading(true);
        cancelRef.current = false;
        try {
            const api = await createAxiosInstance();
            const params: Record<string, unknown> = {pageSize: 200, sortBy: 'rating', sortDir: 'desc'};
            if (selectedOwnerId) params.ownerId = selectedOwnerId;
            if (search.trim()) params.search = search.trim();
            if (categoryIds.length > 0) params.categoryIds = categoryIds;

            const res = await api.get<PageResponse>('/clothing/items', {params: toQueryParams(params)});
            if (!cancelRef.current) setStepItems(res.data.content ?? []);
        } catch {
            if (!cancelRef.current) setStepItems([]);
        } finally {
            if (!cancelRef.current) setStepLoading(false);
        }
    }, [refsLoaded, selectedOwnerId, categoryRefs]);

    useEffect(() => {
        if (currentStep < STEPS.length) {
            setStepSearch('');
            loadStepItems(currentStep, '');
        }
    }, [currentStep, loadStepItems]);

    // Режим замены: как только подгрузились вещи нужного слота — выбираем кандидата.
    useEffect(() => {
        if (!replaceItemId || replaceAppliedRef.current || !replaceSlot) return;
        const stepKey = SLOT_STEP[replaceSlot];
        if (!stepKey || STEPS[currentStep]?.key !== stepKey) return;
        const candidate = stepItems.find(i => i.id === replaceItemId);
        if (!candidate) return;
        replaceAppliedRef.current = true;
        setSelections(prev => (STEPS[currentStep].multiple
            ? {...prev, [stepKey]: [...prev[stepKey].filter(i => i.id !== candidate.id), candidate]}
            : {...prev, [stepKey]: [candidate]}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepItems, currentStep, replaceItemId, replaceSlot]);

    const handleSearchChange = (value: string) => {
        setStepSearch(value);
        loadStepItems(currentStep, value);
    };

    // ── подсказки под текущий слот по всему комплекту (F08) ─────────────────────
    //
    // Раньше подсказки считались парно к последней выбранной вещи («Подходит к: футболка»),
    // из-за чего на шаге обуви предлагались футболки. Теперь состав уходит целиком в
    // `POST /compatibility/for-set`, а слот фильтрует кандидатов на бэкенде.
    const stepKeyForFetch = currentStep < STEPS.length ? STEPS[currentStep].key : 'done';

    useEffect(() => {
        if (!selectedIdsKey) {
            setSetInfo(null);
            setCompatError(null);
            setCompatLoading(false);
            return;
        }
        let cancelled = false;
        setCompatLoading(true);
        setCompatError(null);
        (async () => {
            try {
                const api = await createAxiosInstance();
                const params = selectedOwnerId ? {ownerId: selectedOwnerId} : {};
                const def = STEPS.find(s => s.key === stepKeyForFetch);
                const res = await api.post<CompatibilitySetResult>('/compatibility/for-set', {
                    itemIds: selectedIdsKey.split(','),
                    slot: def ? STEP_SLOT[def.key] : null,
                    categoryCodes: def ? def.categoryCodes : [],
                    limit: 20,
                    warningsOnly: !def,
                }, {params});
                if (!cancelled) setSetInfo(res.data);
            } catch {
                if (!cancelled) {
                    setSetInfo(null);
                    setCompatError('Не удалось подобрать вещи к комплекту');
                }
            } finally {
                if (!cancelled) setCompatLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedIdsKey, stepKeyForFetch, selectedOwnerId, compatReload]);

    const toggleSelection = (item: WardrobeItem) => {
        if (currentStep >= STEPS.length) return;
        const def = STEPS[currentStep];
        const key = def.key;
        const current = selections[key];
        let next: WardrobeItem[];
        if (def.multiple) {
            const already = current.some(i => i.id === item.id);
            next = already ? current.filter(i => i.id !== item.id) : [...current, item];
        } else {
            next = current.some(i => i.id === item.id) ? [] : [item];
        }
        setSelections({...selections, [key]: next});
    };

    const isSelected = (item: WardrobeItem): boolean => {
        if (currentStep >= STEPS.length) return false;
        return selections[STEPS[currentStep].key].some(i => i.id === item.id);
    };

    /** Основное действие подсказки: поставить кандидата в текущий слот (F01/F08). */
    const pickCandidate = (candidate: CompatCandidate) => {
        if (currentStep >= STEPS.length) return;
        const def = STEPS[currentStep];
        const item: WardrobeItem = {
            id: candidate.id,
            name: candidate.name,
            brandName: candidate.brandName,
            categoryId: candidate.categoryId ?? null,
            categoryName: candidate.categoryName,
            firstPhotoUrl: candidate.primaryPhotoUrl,
            rating: null,
        };
        const current = selections[def.key];
        const next = def.multiple
            ? [...current.filter(i => i.id !== item.id), item]
            : [item];
        setSelections({...selections, [def.key]: next});
    };

    /** Отдельное действие «Подробнее»: черновик уже в sessionStorage, возврат его вернёт. */
    const openCandidateDetails = (candidate: CompatCandidate) => {
        navigate(`/clothing/${candidate.id}`);
    };

    /** Переход к слоту вещи, на которую жалуется сигнал пригодности (F05). */
    const replaceItem = (itemId: string) => {
        const idx = STEPS.findIndex(s => selections[s.key].some(i => i.id === itemId));
        if (idx >= 0) {
            setStepHint(null);
            setCurrentStep(idx);
        }
    };

    const removeItem = (itemId: string) => {
        const stepIdx = STEPS.findIndex(s => selections[s.key].some(i => i.id === itemId));
        if (stepIdx < 0) return;
        const key = STEPS[stepIdx].key;
        setSelections({...selections, [key]: selections[key].filter(i => i.id !== itemId)});
    };

    /** «Начать заново»: черновик стирается явно, чтобы не всплыл при следующем заходе (F01). */
    const resetBuilder = () => {
        setSelections(emptySelections<WardrobeItem>());
        setWearStates({});
        setOutfitName('');
        setCurrentStep(0);
        setStepHint(null);
        setSetInfo(null);
        clearDraft();
    };

    // ── живая оценка OutfitScore (outfit-score-engine, FR-8): debounce-пересчёт состава;
    //    wear-state (фаза 4): состояние ношения уходит в запрос, переключатели пересчитывают балл ──
    interface StateEditHint {
        type: string;
        itemOut: string | null;
        itemOutName: string | null;
        edit: string | null;
        delta: number;
    }
    interface LiveScore {
        score: number | null;
        status: string;
        label: string | null;
        confidence: number | null;
        diagnostics: OutfitScoreDiagnostic[];
        counterfactuals?: StateEditHint[];
    }
    const [liveScore, setLiveScore] = useState<LiveScore | null>(null);
    const [scoreLoading, setScoreLoading] = useState(false);
    const [scoreError, setScoreError] = useState<string | null>(null);
    const scoreRequestRef = useRef(0);

    const scoreKey = STEPS
        .map(s => selections[s.key]
            .map(i => `${STEP_SLOT[s.key]}:${i.id}:${JSON.stringify(toWearStatePayload(wearStates[i.id]) ?? null)}`)
            .join(','))
        .filter(Boolean)
        .join('|');

    useEffect(() => {
        if (!scoreKey) {
            setLiveScore(null);
            setScoreError(null);
            setScoreLoading(false);
            return;
        }
        const requestId = ++scoreRequestRef.current;
        setScoreLoading(true);
        setScoreError(null);
        const timer = window.setTimeout(async () => {
            try {
                const api = await createAxiosInstance();
                const items = STEPS.flatMap(s => selections[s.key]
                    .map(i => ({itemId: i.id, slot: STEP_SLOT[s.key], wearState: toWearStatePayload(wearStates[i.id])})));
                const res = await api.post<LiveScore>('/outfits/score', {items, strictMode: false});
                if (scoreRequestRef.current === requestId) {
                    setLiveScore(res.data);
                }
            } catch {
                if (scoreRequestRef.current === requestId) {
                    setLiveScore(null);
                    setScoreError('Не удалось оценить образ');
                }
            } finally {
                if (scoreRequestRef.current === requestId) {
                    setScoreLoading(false);
                }
            }
        }, 400);
        return () => window.clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scoreKey]);

    // ── wear-state (FR-5): применение STATE_EDIT-подсказки в один клик ──────────────────────────
    const STATE_EDIT_LABELS: Record<string, string> = {
        OPEN: 'Расстегнуть', CLOSE: 'Застегнуть', TUCK: 'Заправить', UNTUCK: 'Выпустить',
        ROLL_SLEEVES: 'Закатать рукава', UNROLL_SLEEVES: 'Опустить рукава',
        CUFF: 'Подвернуть низ', UNCUFF: 'Убрать подвороты',
    };
    const STATE_EDIT_PATCHES: Record<string, Partial<WearState>> = {
        OPEN: {closureFraction: 0}, CLOSE: {closureFraction: 1},
        TUCK: {tucking: 'full'}, UNTUCK: {tucking: 'none'},
        ROLL_SLEEVES: {sleeveState: 'rolled'}, UNROLL_SLEEVES: {sleeveState: 'normal'},
        CUFF: {cuffState: 'rolled'}, UNCUFF: {cuffState: 'normal'},
    };

    const applyStateEdit = (hint: StateEditHint) => {
        if (!hint.itemOut || !hint.edit || !STATE_EDIT_PATCHES[hint.edit]) return;
        const itemId = hint.itemOut;
        setWearStates(prev => ({
            ...prev,
            [itemId]: {...(prev[itemId] ?? {}), ...STATE_EDIT_PATCHES[hint.edit as string], wearStateVersion: 'ws-v0'},
        }));
    };

    const stateEditHints = (liveScore?.counterfactuals ?? [])
        .filter(c => c.type === 'STATE_EDIT' && c.edit && c.itemOut && c.delta > 0);

    const renderStateEditChips = () => {
        if (stateEditHints.length === 0) return null;
        return (
            <div className="flex flex-wrap gap-1.5" data-testid="state-edit-hints">
                {stateEditHints.map(hint => (
                    <button
                        key={`${hint.itemOut}-${hint.edit}`}
                        type="button"
                        onClick={() => applyStateEdit(hint)}
                        className="min-h-[44px] px-3 rounded-xl border border-brand-300 dark:border-brand-500/40
                            bg-brand-50 dark:bg-brand-500/10 text-xs font-medium text-brand-700 dark:text-brand-300
                            hover:bg-brand-100 dark:hover:bg-brand-500/20 transition-colors"
                    >
                        {STATE_EDIT_LABELS[hint.edit as string] ?? hint.edit}
                        {hint.itemOutName ? ` «${hint.itemOutName}»` : ''}: +{hint.delta}
                    </button>
                ))}
            </div>
        );
    };

    const saveOutfit = async () => {
        if (allSelectedItems.length === 0) return;
        setSaveStatus('saving');
        try {
            const api = await createAxiosInstance();
            const params = selectedOwnerId ? {ownerId: selectedOwnerId} : {};
            // Состав со слотами и порядком (новая модель).
            const items: {itemId: string; slot: string; sortOrder: number; wearState?: WearState}[] = [];
            let order = 0;
            for (const step of STEPS) {
                for (const it of selections[step.key]) {
                    items.push({
                        itemId: it.id, slot: STEP_SLOT[step.key], sortOrder: order++,
                        wearState: toWearStatePayload(wearStates[it.id]),
                    });
                }
            }
            const body = {name: outfitName.trim() || 'Образ из конструктора', items};
            if (editOutfitId) {
                await api.put(`/outfits/saved/${editOutfitId}`, body, {params});
            } else {
                await api.post('/outfits/saved', body, {params});
            }
            // Работа доведена до конца — черновик больше не нужен (F01).
            clearDraft();
            setSaveStatus('saved');
            showToast(editOutfitId ? 'Образ обновлён' : 'Образ сохранён', 'success');
            const dest = editOutfitId ? `/outfits/saved/${editOutfitId}` : '/outfits/saved';
            setTimeout(() => navigate(dest), 1200);
        } catch {
            setSaveStatus('error');
        }
    };

    // ── render helpers ──────────────────────────────────────────────────────────

    const goToStep = (idx: number) => {
        const blocking = blockingStep(selections, idx);
        if (blocking) {
            setStepHint(`Сначала выберите: ${blocking.label}`);
            return;
        }
        setStepHint(null);
        setCurrentStep(idx);
    };

    const renderStepTabs = () => (
        <div>
            <div className="flex items-center gap-0 mb-2 overflow-x-auto no-scrollbar">
                {[...STEPS.map(s => s.label), 'Готово'].map((label, idx) => {
                    const isDone = idx < currentStep;
                    const isActive = idx === currentStep;
                    // Текущий шаг всегда «доступен»: даже если состав только что изменился,
                    // пользователь уже на нём — помечать его заблокированным нечестно.
                    const blocking = isActive ? null : blockingStep(selections, idx);
                    const reachable = !blocking;
                    return (
                        <React.Fragment key={label}>
                            <button
                                type="button"
                                data-testid={`builder-step-${idx}`}
                                onClick={() => goToStep(idx)}
                                aria-disabled={!reachable}
                                aria-current={isActive ? 'step' : undefined}
                                aria-label={`Шаг ${idx + 1}: ${label}`}
                                title={reachable ? undefined : `Сначала выберите: ${blocking.label}`}
                                className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium whitespace-nowrap transition-colors rounded-lg
                                    ${isActive
                                        ? 'bg-brand-500 text-white'
                                        : reachable
                                            ? `${isDone ? 'text-brand-400' : 'text-gray-600 dark:text-gray-300'} cursor-pointer hover:text-brand-300`
                                            : 'text-gray-400 dark:text-gray-600 cursor-not-allowed'}`}
                            >
                                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0
                                    ${isActive ? 'bg-white/20' : isDone ? 'bg-brand-500/30' : 'bg-gray-200 dark:bg-ink-700'}`}>
                                    {isDone ? <CheckCheckIcon className="w-2.5 h-2.5"/> : idx + 1}
                                </span>
                                {label}
                            </button>
                            {idx < STEPS.length && (
                                <div className={`h-px w-3 mx-0.5 flex-shrink-0 ${idx < currentStep ? 'bg-brand-500' : 'bg-gray-200 dark:bg-ink-700'}`}/>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
            {stepHint && (
                <p role="status" data-testid="builder-step-hint"
                   className="mb-3 text-xs text-amber-700 dark:text-amber-400">
                    {stepHint}
                </p>
            )}
        </div>
    );

    const renderItemCard = (item: WardrobeItem) => {
        const selected = isSelected(item);
        return (
            <button
                key={item.id}
                type="button"
                onClick={() => toggleSelection(item)}
                className={`relative text-left rounded-xl border overflow-hidden pressable
                    ${selected
                        ? 'border-brand-500 ring-2 ring-brand-500/30 bg-brand-50 dark:bg-brand-500/10'
                        : 'border-gray-200 dark:border-ink-700 hover:border-brand-400'
                    }`}
            >
                <div className="aspect-square bg-gray-100 dark:bg-ink-800">
                    {item.firstPhotoUrl ? (
                        <AuthImage imageKey={item.firstPhotoUrl} alt={item.name} variant="thumbnail"
                                   className="w-full h-full object-cover"/>
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300 dark:text-ink-600">
                            <SparklesIcon className="w-8 h-8"/>
                        </div>
                    )}
                </div>
                {selected && (
                    <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-brand-500 flex items-center justify-center">
                        <CheckCheckIcon className="w-3 h-3 text-white"/>
                    </div>
                )}
                <div className="p-2">
                    <div className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">{item.name}</div>
                    {item.categoryName && (
                        <div className="text-xs text-gray-400 dark:text-gray-500 truncate">{item.categoryName}</div>
                    )}
                </div>
            </button>
        );
    };

    const stepDefForPanel = currentStep < STEPS.length ? STEPS[currentStep] : null;

    const renderCompatPanel = () => (
        <BuilderCompatPanel
            candidates={setInfo?.items ?? []}
            loading={compatLoading}
            error={compatError}
            onRetry={() => setCompatReload(v => v + 1)}
            setSize={totalSelected}
            stepLabel={stepDefForPanel ? stepDefForPanel.label : null}
            slotFilled={!!stepDefForPanel && selections[stepDefForPanel.key].length > 0}
            onPick={pickCandidate}
            onDetails={openCandidateDetails}
        />
    );

    // R11: комплект считается целым, только когда закрыты обязательные шаги (верх/низ/обувь).
    const requiredSlotsFilled = STEPS.filter(step => step.required)
        .every(step => selections[step.key].length > 0);

    const renderSignals = () => (
        <OutfitReadinessSignals
            score={liveScore?.score}
            scoreLabel={liveScore?.label}
            scoreLoading={scoreLoading}
            scoreError={scoreError}
            sharedOccasions={setInfo?.sharedOccasions ?? []}
            sharedSeasons={setInfo?.sharedSeasons ?? []}
            occasionDataComplete={setInfo?.occasionDataComplete ?? false}
            seasonDataComplete={setInfo?.seasonDataComplete ?? false}
            warnings={setInfo?.setWarnings ?? []}
            setLoading={compatLoading && !setInfo}
            onReplace={replaceItem}
            complete={requiredSlotsFilled}
        />
    );

    const renderSelectionStrip = () => {
        if (allSelectedItems.length === 0) return null;
        return (
            <div className="flex items-center gap-1.5 flex-wrap">
                {allSelectedItems.map(item => (
                    <div key={item.id} className="relative group">
                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 dark:bg-ink-700 border-2 border-brand-500/60">
                            {item.firstPhotoUrl ? (
                                <AuthImage imageKey={item.firstPhotoUrl} alt={item.name} variant="thumbnail"
                                           className="w-full h-full object-cover"/>
                            ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                    <SparklesIcon className="w-4 h-4 text-gray-300"/>
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={() => removeItem(item.id)}
                            aria-label={`Убрать «${item.name}» из образа`}
                            className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-gray-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                        >
                            <XIcon className="w-2 h-2"/>
                        </button>
                    </div>
                ))}
            </div>
        );
    };

    // ── done step ───────────────────────────────────────────────────────────────

    const hasLimitations = (setInfo?.setWarnings ?? []).length > 0;

    const renderDoneStep = () => (
        <div className="space-y-6">
            <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {totalSelected === 0
                        ? 'Образ пуст'
                        : hasLimitations ? 'Ваш образ готов — с ограничениями' : 'Ваш образ готов'}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    {totalSelected > 0
                        ? `${pluralItems(totalSelected)} в образе`
                        : 'Вернитесь назад и выберите хотя бы одну вещь.'}
                </p>
            </div>
            {totalSelected > 0 && (
                <>
                    {renderSignals()}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                        {allSelectedItems.map(item => (
                            <div key={item.id} className="rounded-xl border border-gray-200 dark:border-ink-700 overflow-hidden">
                                <div className="aspect-square bg-gray-100 dark:bg-ink-800">
                                    {item.firstPhotoUrl ? (
                                        <AuthImage imageKey={item.firstPhotoUrl} alt={item.name} variant="thumbnail"
                                                   className="w-full h-full object-cover"/>
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <SparklesIcon className="w-8 h-8 text-gray-300"/>
                                        </div>
                                    )}
                                </div>
                                <div className="p-2">
                                    <div className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">{item.name}</div>
                                    {item.categoryName && <div className="text-xs text-gray-400 truncate">{item.categoryName}</div>}
                                </div>
                                {/* wear-state: переключатели доступных состояний по capabilities категории */}
                                {item.categoryId && capabilities[item.categoryId] && (
                                    <div className="px-2 pb-2">
                                        <WearStateControls
                                            capability={capabilities[item.categoryId]}
                                            value={wearStates[item.id] ?? null}
                                            onChange={next => setWearStates(prev => ({...prev, [item.id]: next}))}
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    {canManage && (
                        <div className="space-y-3">
                            <input
                                value={outfitName}
                                onChange={e => setOutfitName(e.target.value)}
                                aria-label="Название образа"
                                placeholder="Название образа (необязательно)"
                                className="w-full sm:w-80 px-3 py-2 rounded-xl border border-gray-300 dark:border-ink-700 bg-white dark:bg-ink-800 text-gray-900 dark:text-gray-100 text-sm"
                            />
                            <div className="flex flex-wrap items-center gap-3">
                                <button
                                    type="button"
                                    onClick={saveOutfit}
                                    disabled={saveStatus === 'saving' || saveStatus === 'saved'}
                                    className="px-5 py-2 rounded-xl bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 text-sm font-medium"
                                >
                                    {saveStatus === 'saving' ? 'Сохранение…' : saveStatus === 'saved' ? 'Сохранено!' : editOutfitId ? 'Обновить образ' : 'Сохранить образ'}
                                </button>
                                {saveStatus === 'saved' && (
                                    <span className="text-sm text-green-600 dark:text-green-400">Переходим в каталог…</span>
                                )}
                                {saveStatus === 'error' && (
                                    <span className="text-sm text-red-500">Не удалось сохранить образ</span>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );

    // ── main render ─────────────────────────────────────────────────────────────

    const isDoneStep = currentStep === DONE_STEP;
    const stepDef = isDoneStep ? null : STEPS[currentStep];
    const stepSelCount = stepDef ? selections[stepDef.key].length : 0;

    return (
        <div className="p-4 md:p-6 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {editOutfitId ? 'Редактирование образа' : 'Конструктор образа'}
                </h1>
                {totalSelected > 0 && (
                    <button
                        type="button"
                        data-testid="builder-reset"
                        onClick={resetBuilder}
                        className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-xl border
                                   border-gray-300 dark:border-ink-700 text-sm text-gray-700 dark:text-gray-200
                                   hover:bg-gray-100 dark:hover:bg-ink-800 transition-colors"
                    >
                        <RefreshIcon className="w-4 h-4"/>
                        Начать заново
                    </button>
                )}
            </div>

            {/* outfit-score-engine: живая оценка сочетаемости собранного состава */}
            {totalSelected > 0 && (
                <div className="space-y-2" data-testid="builder-live-score">
                    <OutfitScoreBadge score={liveScore?.score} label={liveScore?.label}
                                      loading={scoreLoading} error={scoreError}/>
                    {!scoreLoading && !scoreError && (
                        <>
                            <OutfitScoreDetails diagnostics={liveScore?.diagnostics ?? []}/>
                            {/* wear-state (FR-5): STATE_EDIT-подсказки «минимальное изменение → прирост» */}
                            {renderStateEditChips()}
                        </>
                    )}
                    {!isDoneStep && renderSignals()}
                </div>
            )}

            {renderStepTabs()}

            {isDoneStep ? renderDoneStep() : (
                <div className="flex gap-6">
                    {/* Left: item grid */}
                    <div className="flex-1 min-w-0 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <div className="flex items-center gap-2">
                                    <h2 className="font-semibold text-gray-900 dark:text-gray-100">{stepDef!.label}</h2>
                                    {stepDef!.required
                                        ? <span className="text-xs font-medium text-red-500 uppercase tracking-wide">обязательно</span>
                                        : <span className="text-xs text-gray-400 dark:text-gray-500">необязательно</span>
                                    }
                                </div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">{stepDef!.description}</p>
                            </div>
                            {stepSelCount > 0 && (
                                <span className="text-xs text-brand-500 font-medium whitespace-nowrap mt-1">
                                    {stepDef!.multiple ? `${stepSelCount} выбрано` : '1 выбрано'}
                                </span>
                            )}
                        </div>

                        <input
                            value={stepSearch}
                            onChange={e => handleSearchChange(e.target.value)}
                            aria-label={`Поиск вещей: ${stepDef!.label}`}
                            placeholder="Поиск…"
                            className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-ink-700 bg-white dark:bg-ink-800 text-gray-900 dark:text-gray-100 text-sm"
                        />

                        {stepLoading ? (
                            <div className="flex justify-center py-12"><Spinner/></div>
                        ) : stepItems.length === 0 ? (
                            <div className="py-12 text-center text-gray-400 dark:text-gray-500 text-sm space-y-2">
                                <SparklesIcon className="w-10 h-10 mx-auto opacity-30"/>
                                <p>Вещей этой категории нет</p>
                                <p className="text-xs">Добавьте вещи или укажите категорию при редактировании</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                                {stepItems.map(item => renderItemCard(item))}
                            </div>
                        )}
                    </div>

                    {/* Right: compatible panel (desktop) */}
                    <aside className="hidden lg:block w-60 xl:w-64 flex-shrink-0">
                        <div className="sticky top-24 rounded-xl border border-gray-200 dark:border-ink-700 p-3 bg-white dark:bg-ink-900">
                            <h3 className="text-xs font-semibold text-gray-900 dark:text-gray-100 mb-2.5 flex items-center gap-1.5">
                                <SparklesIcon className="w-3.5 h-3.5 text-brand-500"/>
                                Совместимые вещи
                            </h3>
                            {renderCompatPanel()}
                        </div>
                    </aside>
                </div>
            )}

            {/* Compatible panel on mobile (only when something is selected) */}
            {!isDoneStep && totalSelected > 0 && (
                <div className="lg:hidden rounded-xl border border-gray-200 dark:border-ink-700 p-3 bg-white dark:bg-ink-900">
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-gray-100 mb-2.5 flex items-center gap-1.5">
                        <SparklesIcon className="w-3.5 h-3.5 text-brand-500"/>
                        Совместимые вещи
                    </h3>
                    {renderCompatPanel()}
                </div>
            )}

            {/* Sticky bottom bar */}
            <div className="sticky bottom-4 z-10">
                <div className="rounded-xl border border-gray-200 dark:border-ink-700 bg-white/95 dark:bg-ink-900/95 backdrop-blur p-3 shadow-lg space-y-2.5">
                    {allSelectedItems.length > 0 && (
                        <div className="space-y-1">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Образ: {pluralItems(totalSelected)}
                            </p>
                            {renderSelectionStrip()}
                        </div>
                    )}
                    <div className="flex items-center gap-2.5">
                        {currentStep > 0 && (
                            <button
                                type="button"
                                onClick={() => setCurrentStep(s => s - 1)}
                                className="px-4 py-1.5 rounded-xl border border-gray-300 dark:border-ink-700 hover:bg-gray-100 dark:hover:bg-ink-700 text-sm"
                            >
                                Назад
                            </button>
                        )}
                        {!isDoneStep && (() => {
                            const isRequired = stepDef?.required ?? false;
                            const hasSelection = stepSelCount > 0;
                            const blocked = isRequired && !hasSelection;
                            return (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => !blocked && setCurrentStep(s => s + 1)}
                                        disabled={blocked}
                                        title={blocked ? 'Выберите вещь для этого шага' : undefined}
                                        className={`px-5 py-1.5 rounded-xl text-sm font-medium transition-colors
                                            ${blocked
                                                ? 'bg-gray-200 dark:bg-ink-700 text-gray-400 cursor-not-allowed'
                                                : 'bg-brand-600 text-white hover:bg-brand-700'
                                            }`}
                                    >
                                        {currentStep === STEPS.length - 1 ? 'К итогу' : 'Далее'}
                                    </button>
                                    {!isRequired && (
                                        <button
                                            type="button"
                                            onClick={() => setCurrentStep(s => s + 1)}
                                            className="text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                        >
                                            Пропустить
                                        </button>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OutfitBuilderPage;
